import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import Select from 'react-select'
import { fetchVodAssets } from '../services/vodApi'
import { Badge, Button, FormattedDateTime, Pagination } from '../components'
import { useVodFilters } from '../hooks'
import { VodAsset, VodPublishStatusCode, VodTypeCode } from '../types/vod'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './VodHighlightsList.module.scss'

const PUBLISH_STATUS_OPTIONS = [
  { value: '', label: 'All Publishing Statuses' },
  { value: 'PUBLISHED', label: 'Published' }, // Includes PUBLISHED_CURRENT and PUBLISHED_OUT_OF_DATE
  { value: 'VIRTUAL_ONLY', label: 'Virtual Only' },
  { value: 'NO_PUBLISH_MESSAGE', label: 'Not Published' },
  { value: 'NO_ACTIVE_PUB_CONTENT', label: 'No Active Content' },
  { value: 'UNLINKED_NO_MATCH', label: 'Unlinked' },
] as const

const VOD_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'HIGHLIGHT', label: 'Highlight' },
  { value: 'CONDENSED', label: 'Condensed' },
] as const

const VodHighlightsList: React.FC = () => {
  const navigate = useNavigate()

  // Get filters from URL state
  const { filters, updateFilterAndResetPage, handlePageChange } = useVodFilters()
  const { vodTypeCode, publishStatus, startDate, endDate, search, matchId, page } = filters
  const limit = 50

  // Fetch VOD assets
  const { data, isLoading, error, mutate } = useSWR(
    ['vod-assets', { vodTypeCode, publishStatus, startDate, endDate, search, matchId, page, limit }],
    () => fetchVodAssets({
      vod_type_code: vodTypeCode || undefined,
      publish_status: publishStatus || undefined,
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      search: search || undefined,
      match_id: matchId || undefined,
      page,
      limit
    }),
    { revalidateOnFocus: false }
  )

  const assets: VodAsset[] = data?.assets || []
  const pagination = data?.pagination

  // Format duration (seconds to MM:SS)
  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  // Publish status display
  const getPublishStatusVariant = (code: VodPublishStatusCode | undefined): 'success' | 'warning' | 'error' | 'neutral' => {
    switch (code) {
      case 'PUBLISHED_CURRENT':
      case 'PUBLISHED_OUT_OF_DATE': // Same as PUBLISHED_CURRENT because we don't require a publish after vod asset ingestion
        return 'success'
      case 'VIRTUAL_ONLY':
      case 'NO_ACTIVE_PUB_CONTENT':
      case 'NO_PUBLISH_MESSAGE':
        return 'warning'
      case 'UNLINKED_NO_MATCH':
        return 'error'
      default:
        return 'neutral'
    }
  }

  const formatPublishStatus = (code: VodPublishStatusCode | undefined): string => {
    if (!code) return '—'
    switch (code) {
      case 'PUBLISHED_CURRENT':
      case 'PUBLISHED_OUT_OF_DATE': // Same as PUBLISHED_CURRENT because we don't require a publish after vod asset ingestion
        return 'Current'
      case 'VIRTUAL_ONLY':
        return 'Virtual'
      case 'NO_ACTIVE_PUB_CONTENT':
      case 'NO_PUBLISH_MESSAGE':
        return 'Not published'
      case 'UNLINKED_NO_MATCH':
        return 'Unlinked'
      default:
        return code
    }
  }

  // Convert "LastName, FirstName" to "F. LastName" format
  const formatPlayerName = (extName: string): string => {
    if (!extName) return ''
    const parts = extName.split(', ')
    if (parts.length === 2) {
      const lastName = parts[0]
      const firstName = parts[1]
      return `${firstName.charAt(0)}. ${lastName}`
    }
    return extName // Return as-is if not in expected format
  }

  // Get display title: "Player1 vs. Player2" from players, else fallback to tc_title
  const getDisplayTitle = (asset: VodAsset): string => {
    // Use player names if exactly 2 players with ext_name
    if (asset.players.length === 2) {
      const name1 = asset.players[0].tc_name || asset.players[0].ext_name
      const name2 = asset.players[1].tc_name || asset.players[1].ext_name
      if (name1 && name2) {
        // Format ext_name from "LastName, FirstName" to "F. LastName"
        const formatted1 = asset.players[0].tc_name ? name1 : formatPlayerName(name1)
        const formatted2 = asset.players[1].tc_name ? name2 : formatPlayerName(name2)
        return `${formatted1} vs. ${formatted2}`
      }
    }
    // Fallback to original title
    return asset.tc_title
  }

  // Clear match filter
  const handleClearMatchFilter = () => {
    updateFilterAndResetPage({ matchId: '' })
  }

  // Early return for error state
  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading highlights</h2>
          <p>Failed to fetch highlights: {(error as Error).message}</p>
          <Button variant="secondary" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>Highlights & Condensed Matches</h1>
      </div>

      {/* Match Filter Banner */}
      {matchId && (
        <div className={styles.matchFilterBanner}>
          <span>
            Showing VODs for match: <code className={styles.matchIdCode}>{matchId}</code>
          </span>
          <Button
            variant="secondary"
            size="small"
            onClick={handleClearMatchFilter}
          >
            Clear Filter
          </Button>
          <Link to={`/matches/${matchId}`}>
            <Button variant="secondary" size="small">
              View Match
            </Button>
          </Link>
        </div>
      )}

      {/* Filters */}
      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          {/* Date Range and Search Filters */}
          <div className={styles.dateFilters}>
            <div className={styles.dateField}>
              <label htmlFor="startDate">Start Date:</label>
              <input
                type="date"
                id="startDate"
                value={startDate}
                onChange={(e) => updateFilterAndResetPage({ startDate: e.target.value })}
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="endDate">End Date:</label>
              <input
                type="date"
                id="endDate"
                value={endDate}
                onChange={(e) => updateFilterAndResetPage({ endDate: e.target.value })}
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="search">Search:</label>
              <input
                type="text"
                id="search"
                value={search}
                onChange={(e) => updateFilterAndResetPage({ search: e.target.value })}
                placeholder="Title, Asset ID, or Description"
                className={listTableStyles.searchInput}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="vodTypeCode">Type:</label>
              <Select
                id="vodTypeCode"
                value={VOD_TYPE_OPTIONS.find(opt => opt.value === vodTypeCode) || VOD_TYPE_OPTIONS[0]}
                onChange={(option) => updateFilterAndResetPage({ vodTypeCode: (option?.value || '') as VodTypeCode | '' })}
                options={VOD_TYPE_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
            <div className={styles.dateField}>
              <label htmlFor="publishStatus">Publishing Status:</label>
              <Select
                id="publishStatus"
                value={PUBLISH_STATUS_OPTIONS.find(opt => opt.value === publishStatus) || PUBLISH_STATUS_OPTIONS[0]}
                onChange={(option) => updateFilterAndResetPage({ publishStatus: (option?.value || '') as VodPublishStatusCode | '' })}
                options={PUBLISH_STATUS_OPTIONS}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
                isClearable={false}
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading highlights...</h3>
          <p>Please wait while we fetch the highlight data.</p>
        </div>
      ) : assets.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No highlights found</h3>
          {matchId ? (
            <p>No VOD highlights or condensed matches are linked to this match.</p>
          ) : (
            <p>No highlights are available for the selected filters.</p>
          )}
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Title</div>
            <div>Competition</div>
            <div>Type</div>
            <div>Duration</div>
            <div>Created</div>
            <div>Match</div>
            <div>Publishing</div>
            <div></div>
          </div>

          <div className={listTableStyles.tableBody}>
            {assets.map((asset) => (
              <div key={asset.tc_vod_asset_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <Link to={`/vod-highlights/${asset.tc_vod_asset_id}`} className={styles.titleLink}>
                    <p className={listTableStyles.primaryText}>{getDisplayTitle(asset)}</p>
                  </Link>
                  {(asset.match?.effective_round ?? asset.match?.tc_round ?? asset.match?.ext_round) && (
                    <p className={listTableStyles.secondaryText}>
                      {asset.match.effective_round ?? asset.match.tc_round ?? asset.match.ext_round}
                    </p>
                  )}
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.match?.competition?.name || 'N/A'}
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.vod_type_display_name || 'N/A'}
                </div>

                <div className={listTableStyles.cellText}>
                  {formatDuration(asset.duration_sec)}
                </div>

                <div className={listTableStyles.cellText}>
                  <FormattedDateTime dateString={asset.created_at} />
                </div>

                <div className={listTableStyles.cellText}>
                  {asset.match?.tc_match_id ? (
                    <Link to={`/matches/${asset.match.tc_match_id}`} className={styles.matchLink}>
                      <Badge variant="success" icon="link">Linked</Badge>
                    </Link>
                  ) : (
                    <Badge variant="error">Unlinked</Badge>
                  )}
                </div>

                <div className={listTableStyles.cellText} title={asset.publish_status}>
                  <Badge variant={getPublishStatusVariant(asset.publish_status)}>
                    {formatPublishStatus(asset.publish_status)}
                  </Badge>
                </div>

                <div className={styles.actionsCell}>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => navigate(`/vod-highlights/${asset.tc_vod_asset_id}`)}
                  >
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {pagination && pagination.total_pages > 1 && (
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}
    </div>
  )
}

export default VodHighlightsList

